import React from "react";
import { Link } from "react-router-dom";
import { bookOfMonth, buyLink } from "../mock";
import { getArticle } from "../articlesData";
import useReveal from "../hooks/useReveal";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const FEATURED_ARTICLE_SLUG = "how-to-develop-self-discipline";

const FeaturedPicks = () => {
  const [ref, visible] = useReveal();
  const article = getArticle(FEATURED_ARTICLE_SLUG);
  const book = bookOfMonth;

  return (
    <section className="bg-[#eceae5] pt-16 md:pt-24 pb-10 md:pb-12" data-testid="featured-picks-section">
      <div
        ref={ref}
        className={`max-w-[1400px] mx-auto px-6 md:px-10 reveal ${visible ? "is-visible" : ""}`}
      >
        <div className="mb-10 md:mb-14">
          <p className="font-sans-ui uppercase tracking-[0.25em] text-sm text-neutral-500">
            Handpicked
          </p>
          <h2
            className="font-display text-3xl md:text-5xl text-neutral-900 mt-3 leading-none tracking-tight"
            style={{ fontWeight: 700 }}
          >
            This month&rsquo;s picks
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Featured article */}
          {article && (
            <Link
              to={`/article/${article.slug}`}
              className="group block"
              data-testid="featured-article-card"
            >
              <p className="font-sans-ui uppercase tracking-[0.2em] text-xs text-neutral-500 mb-4">
                Featured article
              </p>
              <div className="relative w-full aspect-[16/11] overflow-hidden rounded-lg">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-105"
                />
              </div>
              <p className="font-sans-ui text-xs uppercase tracking-widest text-neutral-500 mt-5">
                {article.category.charAt(0) + article.category.slice(1).toLowerCase()} &middot;{" "}
                {article.date} &middot; {article.readTime}
              </p>
              <h3
                className="font-display text-2xl md:text-[32px] text-neutral-900 mt-3 leading-tight group-hover:opacity-70 transition-opacity"
                style={{ fontWeight: 700 }}
              >
                {article.title}
              </h3>
              <p className="font-display text-base md:text-lg text-neutral-700 mt-3 leading-relaxed line-clamp-3">
                {article.excerpt}
              </p>
              <span className="inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-900 mt-5">
                Read the article
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          )}

          {/* Featured book */}
          {book && (
            <div data-testid="featured-book-card">
              <p className="font-sans-ui uppercase tracking-[0.2em] text-xs text-neutral-500 mb-4">
                Book of the month
              </p>
              <Link to={`/resources/${book.slug}`} className="group block">
                <div
                  className="relative w-full aspect-[16/11] overflow-hidden rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${book.color}14` }}
                >
                  <img
                    src={book.cover}
                    alt={book.title}
                    className="h-[78%] w-auto object-contain drop-shadow-2xl transition-transform duration-500 group-hover:-translate-y-1.5 group-hover:scale-[1.03]"
                  />
                  <span
                    className="absolute top-4 left-4 font-sans-ui text-[11px] uppercase tracking-widest text-white px-3 py-1 rounded-full"
                    style={{ backgroundColor: book.color }}
                  >
                    {book.tag}
                  </span>
                </div>
                <h3
                  className="font-display text-2xl md:text-[32px] text-neutral-900 mt-5 leading-tight group-hover:opacity-70 transition-opacity"
                  style={{ fontWeight: 700 }}
                >
                  {book.title}
                </h3>
              </Link>
              <p className="font-display italic text-neutral-600 mt-1">by {book.author}</p>
              <p className="font-display text-base md:text-lg text-neutral-700 mt-3 leading-relaxed line-clamp-3">
                {book.blurb}
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-5">
                <Link
                  to={`/resources/${book.slug}`}
                  data-testid="featured-book-cta"
                  className="inline-flex items-center gap-2 font-sans-ui text-sm text-white bg-neutral-900 hover:bg-neutral-700 px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.03]"
                >
                  Explore the book
                  <ArrowRight size={15} />
                </Link>
                <a
                  href={buyLink(book)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-900 border border-neutral-400 hover:bg-neutral-900 hover:text-white px-6 py-3 rounded-full transition-all duration-300"
                >
                  Find this book
                  <ArrowUpRight size={15} />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedPicks;
